import { QuestionnaireCategoryItemsItemOneOf as Question, QuestionnaireResponseQuestionResponseCurrentAnswer } from '@/api/generated-catch-backend/model';
import { QuestionnaireCard } from '../questionnaire-card/questionnaire-card';
import { Header } from '../questionnaire-header/header';
import { QuestionnaireWrapper } from '../questionnaire-wrapper';
import { BaseNextButton } from '../survey-item/components/next-button/base-next-button';
import { QuestionnaireProvider } from '../../context/QuestionnaireContext';
import { FormProvider, useForm } from 'react-hook-form';
import { useState } from 'react';
import { extractAnswerValue } from '../../hooks/use-questionnaire-form';
import { useUpdateHealthDataPoint } from '@/app/components/health-data/use-health-data';

const getDefaultValueForQuestion = (question: Question, currentAnswer: QuestionnaireResponseQuestionResponseCurrentAnswer) => {
  if (!currentAnswer) return null;

  const extractedValue = extractAnswerValue(currentAnswer.value)

  if (question.questionType === 'select' || question.questionType === 'radio') {
    return Array.isArray(extractedValue) && extractedValue.length > 0 ? extractedValue[0] : '';
  } else if (question.questionType === 'multiselect' || question.questionType === 'checkbox') {
    return Array.isArray(extractedValue) ? extractedValue : [];
  }

  // For all other question types, return the extracted value as-is
  return extractedValue;
};

interface QuestionnaireEditModalContentProps {
  currentAnswer: QuestionnaireResponseQuestionResponseCurrentAnswer,
  question: Question;
  healthDataPointSlug: string;
  title?: string;
  subtitle?: string;
  onClose?: () => void;
}

function QuestionnaireEditModalContent({
  currentAnswer,
  question,
  healthDataPointSlug,
  title = "Edit Health Data",
  subtitle,
  onClose
}: QuestionnaireEditModalContentProps) {
  const [isWhyWeAskExpanded, setIsWhyWeAskExpanded] = useState(false);
  const { updateHealthDataPoint, isPending } = useUpdateHealthDataPoint();

  const form = useForm({
    defaultValues: {
      [question.slug]: getDefaultValueForQuestion(question, currentAnswer),
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    const answerValue = data[question.slug];

    // Skip update if value is null or undefined
    if (answerValue === null || answerValue === undefined) {
      console.error('No answer value provided');
      return;
    }

    updateHealthDataPoint({
      healthDataPointSlug,
      value: answerValue,
      question,
      onSuccess: () => {
        onClose?.();
      },
      onError: (error) => {
        console.error('Failed to update health data:', error);
      },
    });
  });

  const handleNext = () => {
    handleSubmit();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <QuestionnaireWrapper
          className="questionnaire-wrapper inset-0 h-screen overflow-hidden"
        >
          <Header
            categoryName={title || "Edit Health Data"}
            subCategoryName={subtitle}
            onBack={onClose || (() => undefined)}
            showProgress={false}
            showCloseIcon={true}
            onClose={onClose}
            isBackDisabled={false}
          />
          <QuestionnaireCard
            isWhyWeAskExpanded={isWhyWeAskExpanded}
            setIsWhyWeAskExpanded={setIsWhyWeAskExpanded}
            currentQuestion={question}
            handleNext={handleNext}
          />
          {!isWhyWeAskExpanded && (
            <div className="fixed-bottom-controls">
              <div className="controls-container">
                <div className="h-8"></div>
              </div>

              <div className="btn-primary-container">
                <BaseNextButton
                  text="Update"
                  disabled={isPending}
                  onNext={() => handleNext()}
                />
              </div>
            </div>
          )}
        </QuestionnaireWrapper>
      </form>
    </FormProvider>
  );
}

interface QuestionnaireEditModalProps {
  currentAnswer: QuestionnaireResponseQuestionResponseCurrentAnswer
  question: Question;
  healthDataPointSlug: string;
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  isOpen?: boolean;
}

export function QuestionnaireEditModal({
  currentAnswer,
  question,
  healthDataPointSlug,
  title,
  subtitle,
  onClose,
  isOpen = true
}: QuestionnaireEditModalProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black">
      <QuestionnaireProvider>
        <QuestionnaireEditModalContent
          currentAnswer={currentAnswer}
          question={question}
          healthDataPointSlug={healthDataPointSlug}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
        />
      </QuestionnaireProvider>
    </div>
  );
}
